import React, {Fragment, useCallback, useEffect, useRef, useState} from "react";
import { useNavigate } from "react-router-dom";

import {getUserInfoData, useAuth} from "@/shared/libs/auth";
import { toaster } from "@/shared/libs/toaster";
import {Button, Classes, IconName, Spinner} from "@blueprintjs/core";

import { Form, Input, InputPassword } from "@/shared/components/Form";
import { useForm } from "@/shared/libs/react-hook-form";

import {getLoginQrCode, scanQrCodeAfterCallback} from "@/shared/api/system/auth/login"

import { loginSchema, LoginCredentialsDTO } from "@/shared/api/system/auth";
import { useNavigation } from "@/shared/libs/navigation";
import type { ResponseType } from "@/shared/types";
import { responseMessageKey } from "@/config";
import {token as tokenStorage} from "@/shared/utils/storage";
import WechatBindModal from "@/features/Auth/WechatBindModal";
import {message} from "antd";
// 导入loadUser方法
import { loadUser } from "@/shared/libs/auth";

// 解析URL参数工具函数
export const parseUrlParams = (url:string) => {
    const params: Record<string, string> = {};
    if(!url)return params;
    const search = url.split('?')[1];
    if (!search) return params;

    search.split('&').forEach((item) => {
        const [key, val] = item.split('=');
        const decodedKey = decodeURIComponent(key || '');
        const decodedVal = val ? decodeURIComponent(val) : '';
        params[decodedKey] = decodedVal;
    });
    return params;
};

export const AuthLogin = () => {
    const { login , isLoggingIn,updateUserCache } = useAuth();
    const [bindModalVisible, setBindModalVisible] = useState(false); // 仅保留绑定弹窗
    const [bindOpenid, setBindOpenid] = useState<string>("");
    const { refetchNavigate } = useNavigation();
    const methods = useForm({
        schema: loginSchema,
    });
    const navigate = useNavigate();

    // 仅保留微信登录加载状态（无倒计时/二维码相关状态）
    const [isWechatLoading, setIsWechatLoading] = useState(false);
    const authWindowRef = useRef<Window | null>(null); // 授权窗口引用

    // 原有：错误提示处理
    const showErrorDialog = useCallback((error: ResponseType) => {
        const errorMsg = error[responseMessageKey];
        if (typeof errorMsg !== "string") return;
        toaster.current?.show({
            message: errorMsg,
            intent: "danger",
            icon: "error" as IconName,
            timeout: 3000,
        });
        throw error;
    }, []);

    // 原有：账号密码登录提交处理
    const handleSubmit = useCallback(async (values: LoginCredentialsDTO) => {
            login(values)
                .then(() => {
                    const fetch = refetchNavigate();
                    toaster.current?.show({
                        message: "账户登录成功，将自动进行跳转",
                        intent: "success",
                        icon: "tick" as IconName,
                        timeout: 1000,
                        onDismiss: async () => {
                            await fetch;
                            navigate("/");
                        },
                    });
                })
                .catch(showErrorDialog);
        },
        [login, showErrorDialog, navigate, refetchNavigate]
    );

    // 核心：微信登录触发（无弹窗、无新浏览器，复用当前页面授权逻辑）
    const handleWechatLogin = useCallback(async () => {
        try {
            setIsWechatLoading(true);
            // 1. 获取微信授权链接
            const res = await getLoginQrCode();
            if (!res?.qrLoginUrl) {
                throw new Error("获取微信授权链接失败");
            }

            // 2. 核心优化：在当前页面打开授权链接（无新窗口）
            // 原理：微信授权链接在当前页面打开时，PC微信仍能监听到并弹授权框
            window.location.href = res.qrLoginUrl;
        } catch (error) {
            console.error("微信登录触发失败：", error);
            toaster.current?.show({
                message: (error as Error).message || "微信登录触发失败，请重试",
                intent: "danger",
                icon: "error" as IconName,
                timeout: 3000,
            });
        } finally {
            setIsWechatLoading(false);
        }
    }, []);

    // 处理微信授权回调结果（授权成功后跳转回登录页时执行）
    const handleWechatLoginResult = useCallback(async () => {
        // 解析URL中的code和state
        const searchParams = new URLSearchParams(window.location.search);
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        console.log("微信回授权回调结果:code:",code,"state:",state);
        if (code && state) {
            try {
                // 调用后端校验授权信息
                const res = await scanQrCodeAfterCallback(code, state);
                console.log("微信登录回调结果：", res);
                console.log("开始加载用户信息");

                console.log("回调返回token:",res.token);
                // 未绑定ERP账号 → 显示绑定弹窗
                if(res?.status === "unbind"){
                    setBindOpenid(res?.openid ?? "");
                    setBindModalVisible(true);
                } else if(res?.token){
                    // 登录成功 → 存储token并跳转
                    if(res.token)tokenStorage.setToken(res.token);
                    const userInfo = await getUserInfoData(); // 加这一句
                    // 3. 更新缓存
                    await updateUserCache(userInfo);
                    const fetch = refetchNavigate();
                    toaster.current?.show({
                        message: "账户登录成功，自动进行跳转",
                        intent: "success",
                        icon: "tick" as IconName,
                        timeout: 1000,
                        onDismiss: async () => {
                            await fetch;
                            navigate("/");
                            // window.location.href = "/"; // 强制刷新，替代navigate
                        },
                    });
                } else {
                    throw new Error("微信登录失败");
                }
            } catch (error) {
                console.error("微信登录校验失败：", error);
                toaster.current?.show({
                    message: (error as Error).message || "微信登录失败，请重试",
                    intent: "danger",
                    icon: "error" as IconName,
                    timeout: 3000,
                });
            } finally {
                // 清除URL中的授权参数，避免重复处理
                window.history.replaceState({}, document.title, window.location.pathname);
            }
        }
    }, [navigate, refetchNavigate]);

    // 组件挂载时检查授权回调参数
    useEffect(() => {
        handleWechatLoginResult();
    }, [handleWechatLoginResult]);

    // 绑定成功回调
    const handleBindSuccess = (async (token:string) => {
        console.log("绑定成功:",token);
        message.success("绑定成功", 2);
        if(token)tokenStorage.setToken(token);
        const userInfo = await getUserInfoData(); // 加这一句
        // 3. 更新缓存
        await updateUserCache(userInfo);
        const fetch = refetchNavigate();
        toaster.current?.show({
            message: "账户登录成功，将自动进行跳转",
            intent: "success",
            icon: "tick" as IconName,
            timeout: 1000,
            onDismiss: async () => {
                await fetch;
                navigate("/");
                // window.location.href = "/"; // 强制刷新，替代navigate
            },
        });
    });

    return (
        <Fragment>
            <div className="mb-4">
                <h3 className="bp4-heading">登录系统</h3>
                <div className={Classes.TEXT_MUTED}>输入账户名称及密码登录到系统</div>
            </div>

            <Form<LoginCredentialsDTO>
                onSubmit={handleSubmit}
                onError={(e) => console.log("表单验证失败：", e)}
                methods={methods}
            >
                <Input
                    label="登录账户"
                    field="empNo"
                    placeholder="请输入用户登录账户"
                />
                <InputPassword
                    label="登录密码"
                    field="password"
                    placeholder="请输入账户登录密码"
                />

                <Button
                    className="mt-4"
                    loading={isLoggingIn}
                    type="submit"
                    fill
                    intent="primary"
                >
                    登录
                </Button>

                {/* ✅ 微信图标登录：加载动画覆盖在图片上，不闪烁 */}
                <div className="mt-5 flex justify-center">
                    <button
                        type="button"
                        onClick={handleWechatLogin}
                        disabled={isWechatLoading}
                        className="relative w-[40px] h-[40px] border-none bg-transparent p-0 cursor-pointer"
                    >
                        {/* 微信图标 */}
                        <img
                            src="/images/weixin-logo.jpg"
                            alt="微信登录"
                            className="w-full h-full object-contain"
                        />

                        {/* 加载遮罩：只在 isWechatLoading 时显示，盖在图片上面 */}
                        {isWechatLoading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-full">
                                <Spinner size={20} />
                            </div>
                        )}
                    </button>
                </div>
            </Form>

            <WechatBindModal
                visible={bindModalVisible}
                onClose={() => setBindModalVisible(false)}
                openid={bindOpenid}
                onBindSuccess={(token) => handleBindSuccess(token)}
            />
        </Fragment>
    );
};

export default AuthLogin;
